import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { endOfDayInZone, partsInZone, startOfDayInZone } from '@cleartask/shared';
import { useTimeZone } from '../session.js';
import { DetalheNaUrl, useDetalheNaUrl } from '../features/tasks/DetalheNaUrl.js';
import { QuickAdd } from '../features/tasks/QuickAdd.js';
import { AgendaView } from '../features/calendar/AgendaView.js';
import { MonthView } from '../features/calendar/MonthView.js';
import { TimeGridView } from '../features/calendar/TimeGridView.js';
import { YearView } from '../features/calendar/YearView.js';
import {
  CALENDAR_VIEWS,
  calendarRange,
  groupByDay,
  parseDropTarget,
  resolveDropDueAt,
  shiftAnchor,
  useCalendarTasks,
  useReschedule,
  viewDef,
  type CalendarViewId,
} from '../features/calendar/useCalendar.js';

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

/**
 * Tela de calendario (spec 9.4).
 *
 * O `DndContext` mora aqui, e nao dentro de cada visao: o alvo de soltura muda
 * de forma entre as visoes (hora, dia inteiro, dia do mes), mas o efeito e
 * sempre o mesmo — reagendar. Uma copia do handler por visao seria tres
 * lugares para a mesma regra de conversao de data.
 */
export function Calendar() {
  const timeZone = useTimeZone();
  // Abre no MES. A visao de semana mostra sete dias e esconde o resto do
  // planejamento — quem abre o calendario costuma estar perguntando "como esta
  // o mes", nao "o que tem nesta semana", que e justamente o que as visoes Hoje
  // e 7 dias da aba Tarefas ja respondem. As outras quatro continuam a um
  // clique.
  const [view, setView] = useState<CalendarViewId>('month');
  const [anchor, setAnchor] = useState(() => Date.now());
  // Endereco por item, como na aba Tarefas (§8m).
  //
  // Sem sufixo: a visao e a ancora sao estado LOCAL, e `/calendario` e
  // `/calendario/:id` renderizam o mesmo componente na mesma posicao da arvore
  // — o React o mantem montado ao trocar de rota, entao abrir e fechar uma
  // tarefa nao devolve a pessoa ao mes. Ha teste travando isso, porque e uma
  // garantia de reconciliacao, e nao uma decisao escrita em algum lugar do
  // codigo: se um dia a rota passar a montar outro componente, a visao se perde
  // sem nada acusar.
  const { detalhe, abrir, fechar, abrirPorId } = useDetalheNaUrl('/calendario');

  const range = useMemo(() => calendarRange(view, anchor, timeZone), [view, anchor, timeZone]);

  const { tasks, now, hasMore, isPending, isError, error } = useCalendarTasks(range);
  const reschedule = useReschedule();

  const [criando, setCriando] = useState(false);
  const painelNovaTarefa = useRef<HTMLDivElement>(null);
  // Abrir o "+" ja poe o cursor no campo: o clique foi o pedido para digitar.
  useEffect(() => {
    if (criando) painelNovaTarefa.current?.querySelector('input')?.focus();
  }, [criando]);

  const byDay = useMemo(() => groupByDay(tasks, timeZone), [tasks, timeZone]);

  /*
    O bloco inteiro arrasta (§8ab), entao o gesto precisa se distinguir do
    toque de abrir e da rolagem: mouse so arrasta depois de 8 px; no toque,
    so com toque longo (250 ms) — como o TickTick. Um PointerSensor unico
    com distancia faria o dedo que rola a grade agarrar o bloco no caminho.
  */
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor),
  );

  function onDragEnd(event: DragEndEvent) {
    const destino = event.over?.id;
    if (typeof destino !== 'string') return;

    const target = parseDropTarget(destino);
    if (!target) return;

    const task = tasks.find((t) => t.id === event.active.id);
    if (!task) return;

    // Toda a aritmetica de data vive em `resolveDropDueAt`, testada a parte.
    // `null` significa "soltou onde ja estava" — nada a gravar.
    const destinoResolvido = resolveDropDueAt(task, target, timeZone);
    if (!destinoResolvido) return;

    reschedule(task, destinoResolvido.dueAt, destinoResolvido.allDay);
  }

  const def = viewDef(view);
  const ancora = partsInZone(timeZone, anchor);

  /*
    Dia em que o "+" cria a tarefa: hoje, se hoje esta no periodo visivel;
    senao, o dia ancorado. Quem navegou ate novembro e aperta "+" esta
    planejando novembro — criar para hoje jogaria a tarefa fora da tela.
  */
  const inicioDeHoje = startOfDayInZone(timeZone, Date.now());
  const diaEmFoco = range.days.includes(inicioDeHoje)
    ? inicioDeHoje
    : startOfDayInZone(timeZone, anchor);
  const focoParts = partsInZone(timeZone, diaEmFoco);

  return (
    <div className="flex h-[calc(100dvh-var(--tabbar-height))] md:h-dvh min-h-0 flex-col gap-2 p-3 md:gap-3 md:p-4">
      {/*
        Cabecalho no desenho do TickTick (§8ab): o mes sozinho a esquerda; a
        direita, a visao num menu ("Mês ▾") e o grupo ‹ Hoje ›. O ano so
        aparece fora do ano corrente — "outubro" basta para o mes que se vive.
      */}
      <header className="flex items-center gap-2">
        <h1 className="mr-auto truncate font-display text-lg font-semibold">
          {/* No Ano o titulo e o proprio ano: um mes ali nao diria nada. */}
          {def.kind === 'year' ? ancora.year : MESES[ancora.month - 1]}
          {def.kind !== 'year' && ancora.year !== partsInZone(timeZone, Date.now()).year
            ? ` ${ancora.year}`
            : ''}
        </h1>

        <SeletorDeVisao view={view} onChange={setView} />

        <div className="flex items-stretch overflow-hidden rounded-sm border border-border">
          <button
            type="button"
            aria-label="Período anterior"
            onClick={() => setAnchor(shiftAnchor(view, anchor, timeZone, -1))}
            className="min-h-touch px-2 text-text-mut hover:bg-surface-2 hover:text-text"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => setAnchor(Date.now())}
            className="min-h-touch border-x border-border px-3 text-sm hover:bg-surface-2"
          >
            Hoje
          </button>
          <button
            type="button"
            aria-label="Próximo período"
            onClick={() => setAnchor(shiftAnchor(view, anchor, timeZone, 1))}
            className="min-h-touch px-2 text-text-mut hover:bg-surface-2 hover:text-text"
          >
            ›
          </button>
        </div>

        <button
          type="button"
          aria-label="Nova tarefa"
          aria-expanded={criando}
          onClick={() => setCriando((valor) => !valor)}
          className="grid min-h-touch min-w-touch place-items-center rounded-sm text-xl leading-none text-brand-blue hover:bg-surface-2"
        >
          +
        </button>
      </header>

      {/*
        Entrada rapida no proprio calendario, como o "+" do TickTick. E o mesmo
        QuickAdd da aba Tarefas — o parser entende "amanha 14h" aqui tambem —,
        so que o prazo padrao e o dia em foco, de dia inteiro. Fica aberta
        depois de adicionar: planejar o mes costuma ser varias tarefas seguidas,
        e a tarefa nova ja aparece na grade logo abaixo.
      */}
      {criando ? (
        <div
          ref={painelNovaTarefa}
          className="flex items-start gap-2 rounded bg-surface p-2 shadow-card"
          onKeyDown={(event) => {
            if (event.key === 'Escape') setCriando(false);
          }}
        >
          <div className="min-w-0 flex-1">
            <QuickAdd
              defaultDueAt={endOfDayInZone(timeZone, diaEmFoco)}
              placeholder={`Nova tarefa em ${focoParts.day} de ${MESES[focoParts.month - 1]}`}
            />
          </div>
          <button
            type="button"
            aria-label="Fechar nova tarefa"
            onClick={() => setCriando(false)}
            className="grid min-h-touch min-w-touch shrink-0 place-items-center rounded-sm text-text-mut hover:bg-surface-2"
          >
            ✕
          </button>
        </div>
      ) : null}

      {isPending ? <div className="h-64 animate-pulse rounded bg-surface-2" /> : null}

      {isError ? (
        <p className="rounded bg-surface p-4 text-sm text-overdue" role="alert">
          {error instanceof Error ? error.message : 'Não foi possível carregar o calendário.'}
        </p>
      ) : null}

      {!isPending && !isError ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          {def.kind === 'grid' ? (
            <TimeGridView days={range.days} byDay={byDay} now={now} onOpen={abrir} />
          ) : null}

          {def.kind === 'month' ? (
            <MonthView
              days={range.days}
              byDay={byDay}
              now={now}
              focusMonth={ancora.month}
              onOpen={abrir}
            />
          ) : null}

          {def.kind === 'year' ? (
            <YearView
              year={ancora.year}
              byDay={byDay}
              now={now}
              truncado={hasMore}
              onPickDay={(dia) => {
                setView('day');
                setAnchor(dia);
              }}
            />
          ) : null}

          {def.kind === 'agenda' ? (
            <AgendaView days={range.days} byDay={byDay} now={now} onOpen={abrir} />
          ) : null}
        </DndContext>
      ) : null}

      <DetalheNaUrl detalhe={detalhe} fechar={fechar} abrirPorId={abrirPorId} />
    </div>
  );
}

/**
 * A visao do calendario num menu, como o "Mês ▾" do TickTick.
 *
 * Era uma fileira de cinco abas, que no celular ocupava uma linha inteira so
 * para trocar de visao — coisa que se faz raramente. O menu e `menuitemradio`:
 * leitor de tela anuncia qual esta marcada, e Esc ou clique fora fecham.
 */
function SeletorDeVisao({
  view,
  onChange,
}: {
  view: CalendarViewId;
  onChange: (view: CalendarViewId) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberto) return;
    function fora(event: PointerEvent) {
      if (!raiz.current?.contains(event.target as Node)) setAberto(false);
    }
    document.addEventListener('pointerdown', fora);
    return () => document.removeEventListener('pointerdown', fora);
  }, [aberto]);

  return (
    <div
      ref={raiz}
      className="relative"
      onKeyDown={(event) => {
        if (event.key === 'Escape') setAberto(false);
      }}
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-label={`Visão: ${viewDef(view).label}`}
        onClick={() => setAberto((valor) => !valor)}
        className="flex min-h-touch items-center gap-1 rounded-sm border border-border px-3 text-sm hover:bg-surface-2"
      >
        {viewDef(view).label}
        <span aria-hidden="true" className="text-[10px] text-text-mut">
          ▾
        </span>
      </button>

      {aberto ? (
        <ul
          role="menu"
          aria-label="Modo de visualização"
          className="absolute right-0 top-full z-30 mt-1 min-w-36 rounded bg-surface py-1 shadow-card ring-1 ring-border"
        >
          {CALENDAR_VIEWS.map((item) => (
            <li key={item.id} role="none">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={item.id === view}
                onClick={() => {
                  onChange(item.id);
                  setAberto(false);
                }}
                className={[
                  'flex min-h-touch w-full items-center justify-between gap-4 px-3 text-left text-sm hover:bg-surface-2',
                  item.id === view ? 'text-brand-blue' : '',
                ].join(' ')}
              >
                {item.label}
                {item.id === view ? <span aria-hidden="true">✓</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
